import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/stores/authStore';
import { login } from '@/features/auth/services/authApi';

export default function LoginPage() {
  const location = useLocation();
  const verified = (location.state as { verified?: boolean })?.verified;
  const passwordReset = (location.state as { passwordReset?: boolean })?.passwordReset;
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const authLogin = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const validateField = (field: string, value: string) => {
    if (field === 'username' && !value.trim()) return 'Username is required.';
    if (field === 'password' && !value) return 'Password is required.';
    return '';
  };

  const handleBlur = (field: string, value: string) => {
    const err = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: err }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    const uErr = validateField('username', username);
    const pErr = validateField('password', password);
    if (uErr) errors.username = uErr;
    if (pErr) errors.password = pErr;
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setError('');
    setLoading(true);
    try {
      const data = await login(username, password);
      authLogin(data.token, data.username, data.role, data.roles, data.permissions, data.refresh_token);
      navigate(data.is_admin ? '/admin' : '/', { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-bold mb-6 text-accent">Sign In</h1>
        <hr className="mb-6" />

        {verified && (
          <p className="text-sm text-accent mb-4">
            Account verified. You can now sign in.
          </p>
        )}

        {passwordReset && (
          <p className="text-sm text-accent mb-4">
            Password reset successfully. Sign in with your new password.
          </p>
        )}

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-xs font-bold mb-1">Username</label>
            <input
              id="username"
              ref={usernameRef}
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setFieldErrors((prev) => ({ ...prev, username: '' })); }}
              onBlur={() => handleBlur('username', username)}
              required
              aria-invalid={!!fieldErrors.username}
              aria-describedby={fieldErrors.username ? 'username-error' : undefined}
              className={`w-full px-2 py-1.5 border text-sm font-mono focus:outline-none focus:border-accent ${fieldErrors.username ? 'border-red-500' : 'border-border'}`}
            />
            {fieldErrors.username && <p id="username-error" className="text-xs text-red-500 mt-1">{fieldErrors.username}</p>}
          </div>
          <div>
            <label htmlFor="password" className="block text-xs font-bold mb-1">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setFieldErrors((prev) => ({ ...prev, password: '' })); }}
              onBlur={() => handleBlur('password', password)}
              required
              aria-invalid={!!fieldErrors.password}
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              className={`w-full px-2 py-1.5 border text-sm font-mono focus:outline-none focus:border-accent ${fieldErrors.password ? 'border-red-500' : 'border-border'}`}
            />
            {fieldErrors.password && <p id="password-error" className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-terminal disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="text-sm text-muted mt-4">
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        <p className="text-sm text-muted mt-2">
          Don't have an account? <Link to="/register">Register</Link>
        </p>
      </div>
    </div>
  );
}
